<template>
  <!--
    Used to manage the collections that are used in videoPostprocessing.
  -->
  <div class="collectionsList">
    <LoadingPlaceholder v-if="loading" />
    
    <template v-else>
      <Grid
        :ref="kgm_ref"
        :style="{height: 'auto'}"
        :data-items="kgm_computedGridItems(collections)"
        :columns="kgm_responsiveColumns()"
        :filterable="true"
        :filter="kgm_filter"
        :pageable="kgm_pagable"
        :page-size="kgm_take"
        :skip="kgm_skip"
        :take="kgm_take"
        :total="kgm_allGridItems(collections)"
        :sortable="{
          allowUnsort: kgm_allowUnsort,
          mode: kgm_sortMode
        }"
        :sort="kgm_sort"
        selected-field="selected"
        @rowclick="kgm_gridOnRowClick"
        @filterchange="kgm_gridFilterChange"
        @pagechange="kgm_gridPageChange"
        @sortchange="kgm_gridSortChange"
      >
        <div
          slot="nameFilter"
          slot-scope="{props, methods}"
          class="input-group"
        >
          <input
            ref="nameFilterInput"
            :value="props.value"
            type="text"
            class="form-control mr-2 rounded-right"
            placeholder="please enter a name"
            @input="(event) => {methods.change({operator: 'contains', field: props.field, value: event.target.value, syntheticEvent: event});}"
          >
          <div
            v-if="props.value"
            class="input-group-append"
          >
            <button
              class="btn btn-light border rounded"
              @click="kgm_resetInput('nameFilterInput', (event) => {methods.change({operator: '', field: '', value: '', syntheticEvent: event});})"
            >
              <font-awesome-icon
                class="gray"
                icon="times"
              />
            </button>
          </div>
        </div>
        <div
          slot="descriptionFilter"
          slot-scope="{props, methods}"
          class="input-group"
        >
          <input
            ref="descriptionFilterInput"
            :value="props.value"
            type="text"
            class="form-control mr-2 rounded-right"
            placeholder="please enter a description"
            @input="(event) => {methods.change({operator: 'contains', field: props.field, value: event.target.value, syntheticEvent: event});}"
          >
          <div
            v-if="props.value"
            class="input-group-append"
          >
            <button
              class="btn btn-light border rounded"
              @click="kgm_resetInput('descriptionFilterInput', (event) => {methods.change({operator: '', field: '', value: '', syntheticEvent: event});})"
            >
              <font-awesome-icon
                class="gray"
                icon="times"
              />
            </button>
          </div>
        </div>
        <template
          slot="optionsTemplate"
          slot-scope="{props}"
        >
          <td :class="props.className">
            <div class="btn-group">
              <button
                class="btn btn-primary btn-sm"
                @click="showSidebarEdit(props.dataItem)"
              >
                <font-awesome-icon
                  class="mr-2"
                  icon="edit"
                />
                <span>Edit</span>
              </button>
              <router-link
                :to="{ path: `/videopostprocessing/collections/${ props.dataItem.id }` }"
                class="btn btn-sm btn-secondary"
              >
                <font-awesome-icon
                  class="gray mr-1"
                  icon="images"
                />
                <span>
                  {{ $t('themeListComp.elements') }}
                </span>
              </router-link>
              <button
                class="btn btn-secondary btn-sm"
                @click="openConfirmModal(props.dataItem.id)"
              >
                <font-awesome-icon
                  class="mr-2 gray"
                  icon="trash"
                />
                <span>Remove</span>
              </button>
            </div>
          </td>
        </template>
      </Grid>
      <Sidebar
        v-if="showSidebar"
        :show-sidebar="showSidebar"
        @close="close"
      >
        <CollectionsAdd
          v-if="addingElement"
          @reload="reloadCollections"
        />
        <CollectionsEdit
          v-else
          :collection="collection"
          @reload="reloadCollections"
        />
      </Sidebar>
    </template>

    <SweetModal
      ref="confirmDelete"
      :title="$t('introOutroListComp.deleteElement')"
      icon="warning"
      blocking
      class="overflowHidden"
    >
      <p>{{ $t('introOutroListComp.sureToDelete') }}</p>
      <button
        slot="button"
        class="btn btn-secondary float-left mb-3"
        @click="$refs.confirmDelete.close()"
      >
        <font-awesome-icon
          class="mr-2"
          icon="times"
        />{{ $t('cancel') }}
      </button>
      <button
        slot="button"
        class="btn btn-danger float-right mb-3"
        @click="removeCollection()"
      >
        <font-awesome-icon
          class="mr-2"
          icon="trash"
        />{{ $t('delete') }}
      </button>
      <div class="clearfix" />
    </SweetModal>
  </div>
</template>

<script>

import { SweetModal } from 'sweet-modal-vue';
import { kendoGridMixin } from '@/mixins/kendoGridMixin.js';
import Sidebar from '@/components/Base/Sidebar.vue';


export default {
  name: "CollectionsList",
  components: {
    SweetModal,
    CollectionsAdd: () => import('@/components/VideoPostprocessing/ImageCollections/CollectionsAdd.vue'),
    CollectionsEdit: () => import('@/components/VideoPostprocessing/ImageCollections/CollectionsEdit.vue'),
    Sidebar
  },
  mixins: [
    kendoGridMixin
  ],
  data () {
    return {
      loading: true,
      collection: null,
      collections: null,
      showSidebar: false,
      addingElement: false,
      currentId: null,
      deleteIndex: null,
      kgm_filter: {
        logic: "and",
        filters: [
          {
            field: 'name',
            operator: 'contains',
            value: ''
          }
        ]
      },
      kgm_sort: [
        {
          "field": "name",
          "dir": "asc"
        }
      ],
      kgm_columns: [
        {
          field: 'name',
          filterable: true,
          filter: 'text',
          title: 'Name',
          filterCell: "nameFilter"
        },
        {
          field: 'description',
          filterable: true,
          filter: 'text',
          title: 'Description',
          filterCell: "descriptionFilter"
        },
        {
          field: 'type',
          filterable: true,
          filter: 'text',
          title: 'Type',
          filterCell: "nameFilter"
        },
        {
          filterable: false,
          title: 'Options',
          cell: 'optionsTemplate',
          width: '300px'
        }
      ]
    }
  },
  metaInfo () {
    return {
      title: this.$t('menu.collections')
    }
  },
  created () {
    this.getCollections();
  },
  methods: {
    //#region Sidebar
    show () {
      this.showSidebar = true;
    },
    close () {
      this.showSidebar = false;
    },
    showSidebarAdd () {
      this.addingElement = true;
      this.show();
    },
    showSidebarEdit (emitValues) {
      this.setCurrentCollection(emitValues);
      this.addingElement = false;
      this.show();
    },
    setCurrentCollection (collection) {
      this.collection = Object.assign({}, collection); //clones Object without reference to fix close sidebar issue
    },
    //#endregion

    //#region API-calls
    reloadCollections () {
      this.showSidebar = false;
      this.$emit("reloadAuditLogs");
      this.getCollections();
    },
    openConfirmModal (index) {
      this.deleteIndex = index; 
      this.$refs.confirmDelete.open();
    },
    removeCollection () {
      this.axios.delete(`/VideoPostProcessing/DeleteImageCollection?collectionId=${ this.deleteIndex }`)
        .then(() => {
          this.getCollections();
          this.$emit("reloadAuditLogs");
        })
        .finally(() => {
          this.deleteIndex = null;
          this.$refs.confirmDelete.close();
        });
    },
    getCollections () {
      this.loading = true;
      this.axios.get('/VideoPostProcessing/GetAllImageCollections')
        .then((response) => {
          if (response == null) {
            return;
          }
          if (response.data == null) {
            return;
          }
          this.collections = response.data;
        })
        .finally(() => {
          this.loading = false;
        });
    }
    //#endregion
  }
}
</script>
<style>
.collectionsList .k-grid table tr:hover td {
  background :rgb(219, 219, 219) !important;
  cursor: pointer !important;
}
</style>